import { computed, reactive } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import { usePage } from '@inertiajs/vue3';

declare global {
	interface Window {
		/** Defined by public.blade.php when GA or GTM is configured. */
		spLoadAnalytics?: () => void;
	}
}

/**
 * Cookies deleted when the visitor withdraws consent: the Google Analytics ones
 * and the UTM attribution cookie (written only with consent, use-utm-tags.ts).
 */
export const UTM_COOKIE_NAME = 'utm_tags';
/** Pre-consent copy of the UTM tags (tab-scoped), removed on withdrawal. */
export const UTM_SESSION_KEY = 'sp_utm_tags';

const GA_COOKIE_PATTERN = new RegExp(
	`^(_ga|_ga_.+|_gid|_gat.*|${UTM_COOKIE_NAME})$`,
);

/**
 * The visitor's choice is read from the consent cookie here, in the browser,
 * never rendered by the server: public pages are sent with long-lived public
 * HTTP cache headers, so their HTML and props are the same for every visitor.
 *
 * The cookie (written by the package's consent routes, see
 * BrowserReadableCookiesManager) is `{ consent_at: <unix>, <cookie name>: bool, … }`.
 */
type StoredConsent = Record<string, unknown> & { consent_at: number };

/** Shared by the banner, the preferences dialog and the "Cookie settings" links. */
const state = reactive({
	stored: undefined as StoredConsent | null | undefined,
	preferencesOpen: false,
	saving: false,
	error: false,
});

/** Keyed so repeated registrations (one per useUtmTags() call) run once. */
const analyticsListeners = new Map<string, () => void>();

/** usePage() builds a new set of computeds on every call: create it once. */
let page: ReturnType<typeof usePage> | undefined;

function config() {
	page ??= usePage();
	return page.props.cookieConsent;
}

function readStoredConsent(): StoredConsent | null {
	const cfg = config();
	if (!cfg) return null;

	const prefix = `${cfg.cookieName}=`;
	const raw = document.cookie
		.split('; ')
		.find((cookie) => cookie.startsWith(prefix));
	if (!raw) return null;

	try {
		const value: unknown = JSON.parse(
			decodeURIComponent(raw.slice(prefix.length).replace(/\+/g, ' ')),
		);
		if (
			typeof value !== 'object' ||
			value === null ||
			typeof (value as StoredConsent).consent_at !== 'number'
		) {
			return null;
		}
		const stored = value as StoredConsent;
		// Same expiry rule as the package (CookiesManager::getCurrentConsentSettings).
		const expiresAt = stored.consent_at + cfg.lifetimeMinutes * 60;
		return expiresAt < Date.now() / 1000 ? null : stored;
	} catch {
		return null;
	}
}

function stored(): StoredConsent | null {
	if (state.stored === undefined) state.stored = readStoredConsent();
	return state.stored;
}

/** The visitor chose, and has seen every analytics cookie registered today. */
function hasDecided(): boolean {
	const consent = stored();
	const names = config()?.analyticsCookies ?? [];
	return consent !== null && names.every((name) => name in consent);
}

function hasAnalyticsConsent(): boolean {
	const consent = stored();
	const names = config()?.analyticsCookies ?? [];
	return (
		consent !== null &&
		names.length > 0 &&
		names.every((name) => consent[name] === true)
	);
}

/**
 * Runs `callback` now if the visitor has consented to analytics, otherwise
 * as soon as they do (in this session).
 */
export function whenAnalyticsAllowed(callback: () => void, key = 'default') {
	// Nothing to consent to (no banner): run right away.
	if (!config()?.configured || hasAnalyticsConsent()) {
		callback();
		return;
	}
	analyticsListeners.set(key, callback);
}

/**
 * Whether analytics data may be recorded now. True when no tool is configured
 * (nothing is loaded, so nothing is sent).
 */
export function isAnalyticsAllowed(): boolean {
	return !config()?.configured || hasAnalyticsConsent();
}

function startAnalytics() {
	window.spLoadAnalytics?.();
	const callbacks = [...analyticsListeners.values()];
	analyticsListeners.clear();
	callbacks.forEach((callback) => callback());
}

/** Deletes the GA cookies on this host and every parent domain GA may have used. */
function deleteAnalyticsCookies() {
	const parts = window.location.hostname.split('.');
	const domains = parts.map((_, i) => parts.slice(i).join('.'));
	try {
		window.sessionStorage.removeItem(UTM_SESSION_KEY);
	} catch {
		// storage unavailable
	}
	const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';

	document.cookie
		.split(';')
		.map((cookie) => cookie.split('=')[0]?.trim() ?? '')
		.filter((name) => GA_COOKIE_PATTERN.test(name))
		.forEach((name) => {
			document.cookie = `${name}=; ${expired}`;
			domains.forEach((domain) => {
				document.cookie = `${name}=; ${expired}; domain=.${domain}`;
			});
		});
}

type ConsentRoute =
	| 'cookieconsent.accept.all'
	| 'cookieconsent.accept.essentials'
	| 'cookieconsent.accept.configuration';

async function submit(routeName: ConsentRoute, payload = {}) {
	const hadAnalytics = hasAnalyticsConsent();

	state.saving = true;
	state.error = false;
	try {
		await axios.post(route(routeName), payload, {
			headers: { Accept: 'application/json' },
		});
	} catch (error) {
		// e.g. 419 when the HTTP-cached page outlived the CSRF cookie: keep the
		// banner/dialog open so the visitor can retry, instead of an unhandled rejection.
		console.error('Saving the cookie consent failed.', error);
		state.error = true;
		return;
	} finally {
		state.saving = false;
	}

	// The response has just written the new consent cookie.
	state.stored = readStoredConsent();
	state.preferencesOpen = false;

	const hasAnalytics = hasAnalyticsConsent();
	if (hasAnalytics && !hadAnalytics) {
		startAnalytics();
	} else if (!hasAnalytics && hadAnalytics) {
		// Scripts already running cannot be unloaded: drop their cookies and reload.
		deleteAnalyticsCookies();
		window.location.reload();
	}
}

/**
 * Loads GA/GTM on page load when the stored consent allows it.
 * Called once by the public app (public-app.ts) after mounting.
 */
export function startAnalyticsIfAllowed() {
	if (config()?.configured && hasAnalyticsConsent()) {
		startAnalytics();
	}
}

export function useCookieConsent() {
	const isConfigured = computed(() => config()?.configured ?? false);

	return {
		isConfigured,
		showBanner: computed(() => isConfigured.value && !hasDecided()),
		analytics: computed(() => hasAnalyticsConsent()),
		preferencesOpen: computed({
			get: () => state.preferencesOpen,
			set: (value: boolean) => (state.preferencesOpen = value),
		}),
		saving: computed(() => state.saving),
		error: computed(() => state.error),
		openPreferences: () => (state.preferencesOpen = true),
		acceptAll: () => submit('cookieconsent.accept.all'),
		rejectAll: () => submit('cookieconsent.accept.essentials'),
		save: (allowAnalytics: boolean) =>
			submit('cookieconsent.accept.configuration', {
				categories: allowAnalytics ? ['analytics'] : [],
			}),
	};
}
